// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useEffect, useContext } from 'react';
import './AggregationsTimeline.scss';

import { AuthContext } from '../../context/AuthContext';
import { firestore } from '../../context/FirebaseConfig';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    Filler
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { de } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

import { subscribeToAggregatedDeviceObservations } from '../../shared_components/utils/ObservationAggregationUtils';
import LoadingIcon from '../LoadingIcon/LoadingIcon';
import { getColorPalette } from '../../utils/colorPaletteUtils';
import { 
    supplierBackgroundPlugin, 
    getSupplierFooterConfig, 
    getSupplierPluginConfig,
    useSupplierTimeframes,
    SupplierLegend
} from './supplierTimelineUtils';
import { getTooltipTitle, getTimeAxisFormat } from './chartUtils';

// Register the components needed for Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    Filler
);

// Custom hover plugin to show vertical line on hover
const verticalHoverPlugin = {
    id: 'verticalHover',
    beforeDraw: (chart) => {
        if (chart.tooltip?.opacity === 0) return;
        
        const { ctx, tooltip, chartArea } = chart;
        if (tooltip.dataPoints?.length > 0) {
            const { x } = tooltip.dataPoints[0].element;
            
            // Save the canvas state
            ctx.save();
            
            // Draw vertical line
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
            ctx.lineWidth = 1;
            ctx.setLineDash([5, 5]);
            
            ctx.beginPath();
            ctx.moveTo(x, chartArea.top);
            ctx.lineTo(x, chartArea.bottom);
            ctx.stroke();
            
            // Restore the canvas state
            ctx.restore();
        }
    }
};

export default function ContaminantsTimeline({ timeframe,
    showSuppliers = false,
    timeIntervalMinutes = 5,
    onTimeSelected,
}) {
    const [observationsData, setObservationsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { currentUser, labelsets, suppliers } = useContext(AuthContext);
    const { t } = useTranslation();

    const supplierTimeframes = useSupplierTimeframes(firestore, suppliers, currentUser.company.company, currentUser.device_id, timeframe, showSuppliers);

    useEffect(() => {
        if (!firestore || !currentUser || !timeframe) {
            return;
        }

        const clientId = currentUser.company.company;
        const deviceId = currentUser.device_id;

        if (!clientId || !deviceId) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);

        const unsubscribe = subscribeToAggregatedDeviceObservations(
            firestore,
            clientId,
            deviceId,
            timeframe.startDate,
            timeframe.endDate,
            timeIntervalMinutes,
            (data) => {
                setObservationsData(data);
                setIsLoading(false);
            }
        );

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [currentUser, timeframe.startDate.getTime(), timeframe.endDate.getTime(), timeIntervalMinutes]);

    const contaminantKeys = React.useMemo(() => Object.keys(observationsData?.reduced_data?.data_per_labelset?.contaminants || {}), [observationsData]);
    const colorPalette = React.useMemo(() => getColorPalette(contaminantKeys.length), [contaminantKeys]);

    const data = React.useMemo(() => {
        return contaminantKeys.reduce((acc, key) => {
            acc[key] = observationsData?.intervals?.map(item => ({
                x: new Date(item.start_timestamp),
                y: item?.data?.data_per_labelset?.contaminants?.[key]?.detection_count
            }));
            return acc;
        }, {});
    }, [observationsData, contaminantKeys]);

    // Format chart data
    const chartData = {
        datasets: contaminantKeys.map((key, index) => ({
            label: labelsets?.contaminants?.labels?.[key]?.display_name || key,
            data: data[key],
            backgroundColor: colorPalette[index],
            borderColor: colorPalette[index],
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: colorPalette[index],
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
        }))
    };

    // Chart options
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        onClick: (event, elements) => {
            if (elements.length > 0 && onTimeSelected) {
                const elementIndex = elements[0].index;
                const dataPoint = observationsData?.intervals?.[elementIndex];
                if (dataPoint) {
                    const startTime = new Date(dataPoint.start_timestamp).getTime();
                    const endTime = new Date(dataPoint.end_timestamp).getTime();
                    const midTimestamp = new Date((startTime + endTime) / 2);
                    onTimeSelected(midTimestamp);
                }
            }
        },
        plugins: {
            legend: {
                position: 'top',
                display: true,
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 20,
                    font: {
                        size: 12
                    }
                }
            },
            title: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'white',
                titleColor: '#333',
                bodyColor: '#666',
                borderColor: '#ddd',
                borderWidth: 1,
                boxPadding: 6,
                boxWidth: 8,
                boxHeight: 8,
                cornerRadius: 4,
                usePointStyle: true,
                padding: 12,
                ...getSupplierFooterConfig(supplierTimeframes, timeIntervalMinutes),
                callbacks: {
                    title: (tooltipItems) => getTooltipTitle(tooltipItems, timeframe, timeIntervalMinutes),
                    label: (tooltipItem) => {
                        return `Detektionen ${tooltipItem.dataset.label}: ${Math.round(tooltipItem.parsed.y)}`;
                    },
                    labelPointStyle: () => {
                        return {
                            pointStyle: 'circle',
                            rotation: 0
                        };
                    },
                    ...getSupplierFooterConfig(supplierTimeframes, timeIntervalMinutes)
                }
            },
            ...getSupplierPluginConfig(supplierTimeframes),
            verticalHover: {}
        },
        scales: {
            x: {
                type: 'time',
                time: getTimeAxisFormat(timeframe),
                title: {
                    display: false
                },
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.05)',
                    drawBorder: false
                },
                adapters: {
                    date: {
                        locale: de,
                    }
                },
                ticks: {
                    maxRotation: 0,
                    autoSkip: true,
                    source: 'data',
                    font: {
                        size: 12
                    }
                }
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: timeIntervalMinutes === 1440 ? 
                        t('timeline_per_unit', { 
                            value: t('timeline_contaminants_count'),
                            unit: t('timeline_time_unit_day')
                        }) : 
                        timeIntervalMinutes === 60 ? 
                            t('timeline_per_unit', {
                                value: t('timeline_contaminants_count'),
                                unit: t('timeline_time_unit_hour')
                            }) :
                            t('timeline_per_unit', {
                                value: t('timeline_contaminants_count'),
                                unit: t('timeline_time_unit_min', { minutes: timeIntervalMinutes })
                            }),
                    font: {
                        size: 14
                    }
                },
                border: {
                    display: false
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                    drawBorder: false,
                    tickLength: 0
                },
                ticks: {
                    padding: 10,
                    font: {
                        size: 12
                    }
                }
            }
        }
    };

    return (
        <div className="aggregations-timeline">
            {isLoading ? (
                <div className="loading"><LoadingIcon color="rgba(0, 0, 0, 0.2)" /></div>
            ) : observationsData?.intervals?.length === 0 ? (
                <div className="no-data">No data available for the selected date</div>
            ) : (
                <div className="chart-container">
                    <div className="chart-container-inner">
                        <Line 
                            data={chartData} 
                            options={options} 
                            plugins={[supplierBackgroundPlugin, verticalHoverPlugin]} 
                        />
                    </div>
                    {showSuppliers && <SupplierLegend supplierTimeframes={supplierTimeframes} />}
                </div>
            )}
        </div>
    );
} 